import { defineStore } from "pinia";
import { getCheckpointVersions } from "@/api/responsibilityAPI";

/**
 * Checkpoint version store.
 *
 * Wraps the checkpoint version history endpoint in a Pinia action so
 * components never call the API module directly (PR checklist §4). The
 * version history drawer and the ER-11249 V{n} badge share this endpoint.
 */
export const useCheckpointVersionsStore = defineStore("checkpointVersions", () => {
  /**
   * Fetch the current published version's display metadata.
   * Best-effort — resolves `null` when the checkpoint has no published versions,
   * or when no explicit `is_current` row is returned (falls back to the newest row).
   *
   * ER-11208: includes `publishMessage` / `publishMessageStatus` so the history
   * icon tooltip can render the "what changed" summary without a second request.
   *
   * @param {number} accountId
   * @param {number} checkpointId
   * @returns {Promise<{versionNumber: number|null, publishMessage: string|null, publishMessageStatus: string|null}|null>}
   */
  async function fetchCurrentVersionMeta(accountId, checkpointId) {
    const response = await getCheckpointVersions(accountId, checkpointId);
    const results = response?.data?.results ?? response?.data ?? [];
    const current = results.find((row) => row.is_current) ?? results[0] ?? null;
    if (!current) return null;
    return {
      versionNumber: current.version_number ?? null,
      publishMessage: current.publish_message ?? null,
      publishMessageStatus: current.publish_message_status ?? null,
    };
  }

  /**
   * Fetch the current published version number for a checkpoint.
   * Best-effort — returns null when the checkpoint has no published
   * versions yet, or when no explicit `is_current` row is returned
   * (falls back to the newest row in the list).
   *
   * @param {number} accountId
   * @param {number} checkpointId
   * @returns {Promise<number|null>}
   */
  async function fetchCurrentVersionNumber(accountId, checkpointId) {
    const meta = await fetchCurrentVersionMeta(accountId, checkpointId);
    return meta?.versionNumber ?? null;
  }

  return { fetchCurrentVersionNumber, fetchCurrentVersionMeta };
});
